/** How Inventory works, in the words its screens use. Everyone who can sign in can read it. */
export function Help() {
  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">How Inventory works</div>
          <div className="page-subtitle">Counting what is on hand, and reading the week's totals</div>
        </div>
      </div>

      <div className="help__body">
        <section className="help__section">
          <h2 className="help__h2">What it is for</h2>
          <p>
            Each week, supervisors count the containers on hand at each location — facilities, route
            trucks and supply units. Those counts add up to what the business is holding, and show the Operations Admin
            what is short so it can be bought. This is a count of what is there, not a record of what moved: nobody
            logs deliveries or usage here.
          </p>
        </section>

        <section className="help__section">
          <h2 className="help__h2">When counts are due</h2>
          <ul className="help__list">
            <li>Counts are weekly, <strong>due end of day Wednesday</strong>. A week runs Thursday to Wednesday.</li>
            <li>A count typed on Thursday for Wednesday's week still belongs to Wednesday's week.</li>
            <li>
              Each type of location has a few <strong>grace days</strong>. A count saved within them is
              <em> Reported</em>; after them it is <em>Reported late</em>. A location with no count is
              <em> Not yet</em> while the week is open and <em>Missing</em> once it has closed.
            </li>
            <li>A type of location can be switched to daily counts in Setup, without anything else changing.</li>
          </ul>
        </section>

        <section className="help__section">
          <h2 className="help__h2">Entering a count</h2>
          <ol className="help__steps">
            <li>Open <strong>Counts</strong>, find the location, and choose <strong>Enter count</strong>.</li>
            <li>Every item on the location's count list needs a number. <strong>Enter 0 when none are on hand.</strong></li>
            <li>
              Have a supply that isn't listed? Choose it under <strong>Add a supply not listed</strong> and count it too.
              It stays on that location's sheet the next week; <strong>Remove</strong> takes it off again.
            </li>
            <li>Add a note for anything unusual, then <strong>Save count</strong>.</li>
          </ol>
          <div className="help__callout">
            <div className="help__callout-label">Why a blank is not allowed</div>
            <p style={{ margin: 0 }}>
              In the spreadsheet, "none on hand" and "nobody counted" both looked like an empty cell, and both added up to
              nothing — so a truck that was never counted looked empty and could trigger a purchase. Here a location is
              asked only for what it carries, and must answer every one.
            </p>
          </div>
          <ul className="help__list">
            <li>Saving again in the same week corrects the count; it never adds a second one. If someone else saved it while you had it open, you are told, and nothing is overwritten.</li>
            <li>Once a week's grace days have passed, only the Operations Admin can correct its counts, and the correction is recorded as theirs.</li>
            <li>The weeks of September 9 and 16, 2026 hold the history imported from the spreadsheet, marked <em>From spreadsheet</em>.</li>
          </ul>
        </section>

        <section className="help__section">
          <h2 className="help__h2">Reading the Dashboard</h2>
          <ul className="help__list">
            <li><strong>Coverage comes first.</strong> "38 of 78 locations reported" means the totals below include only those 38.</li>
            <li>Each row is a supply; each column is a group. <strong>Total</strong> adds the groups together.</li>
            <li>
              <strong>Minimum</strong> adds up the minimum on-hand targets of the locations that reported and carry the item.
              The status is <strong>SHORT</strong> when the total is below it, <strong>OK</strong> when it is not, and
              <strong> SET TARGET</strong> when no target has been set — never a silent zero.
            </li>
            <li>
              <strong>On hand by</strong> switches between <strong>Supply</strong> and <strong>Location</strong>. By
              location, pick a location to see every supply it counted, and any on its list it left out. By supply,
              pick it from the list. Every location that counted it
              is listed lowest against its minimum first; a red number is below that location's own minimum, and each
              bar shows the count against that minimum, with how many short.
            </li>
            <li>Both tables export to a file that opens in Excel.</li>
          </ul>
        </section>

        <section className="help__section">
          <h2 className="help__h2">Setup</h2>
          <ul className="help__list">
            <li><strong>Locations</strong> — every place that is counted, with its region, group and type. Renaming a location keeps its history. A location that stops being counted is made inactive, never deleted.</li>
            <li><strong>Supplies</strong> — what can be counted, how many locations count each one, and for each type of location what it is asked to count, how often, and its grace days.</li>
            <li>
              <strong>Targets</strong> — minimum on hand per location. Set one for every location, then override it for a
              group or a single location where that differs; the most specific target wins. Targets start today or
              later, so a change never rewrites whether a past week was short.
            </li>
          </ul>
        </section>

        <section className="help__section">
          <h2 className="help__h2">Who can do what</h2>
          <ul className="help__list">
            <li>Access is given in Admin's Administration screen, by <strong>region</strong>: East, Heartland or Midwest, or all regions.</li>
            <li><strong>Operations Admin</strong> sees every region, enters and corrects counts, and maintains Setup.</li>
            <li><strong>Driver Supervisor</strong> enters counts for their regions and sees those regions' totals.</li>
            <li><strong>Viewer</strong> sees counts and totals for their regions.</li>
            <li>Changing Setup needs it at all regions, because a count list or a target changes what every region is measured against.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}
